import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Bot, Loader2, ArrowRight, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function CommandBar({ compact = false }) {
    const { currentBusiness } = useApp();
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const nav = useNavigate();

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed || !currentBusiness) return;
        setBusy(true);
        try {
            const { message } = await api.chat(currentBusiness.id, trimmed);
            // Stash last message so AI Manager can display it immediately
            sessionStorage.setItem(
                "ll_last_ai",
                JSON.stringify({
                    userText: trimmed,
                    assistant: message,
                }),
            );
            setText("");
            nav("/ai-manager");
        } catch (e) {
            toast.error("AI is temporarily unavailable — please retry.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="ai-command-bar"
            className={`${
                compact
                    ? "static"
                    : expanded
                        ? "fixed bottom-4 sm:bottom-6 left-3 right-3 sm:left-6 sm:right-6 lg:left-72 lg:right-8 z-30 max-w-4xl mx-auto"
                        : "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 w-auto"
            }`}
        >
            {!compact && !expanded ? (
                <button
                    type="button"
                    data-testid="ai-command-bar-expand"
                    onClick={() => setExpanded(true)}
                    aria-label="Expand AI Manager"
                    className="flex items-center gap-2 rounded-2xl border border-indigo-400/30 bg-[#151b35] px-4 py-3 text-sm font-semibold text-white shadow-xl shadow-indigo-500/20 hover:bg-[#20294a]"
                >
                    <Sparkles className="h-4 w-4 text-indigo-300" />
                    AI Manager
                    <ChevronUp className="h-4 w-4 text-indigo-300" />
                </button>
            ) : (
            <div className="relative surface glow-indigo rounded-2xl backdrop-blur-xl">
                {!compact && (
                    <div className="flex justify-end px-3 pt-2">
                        <button
                            type="button"
                            data-testid="ai-command-bar-minimize"
                            onClick={() => setExpanded(false)}
                            disabled={busy}
                            aria-label="Minimize AI Manager"
                            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-40"
                        >
                            <ChevronDown className="h-4 w-4" />
                            Minimize
                        </button>
                    </div>
                )}
                <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className="hidden sm:flex items-center gap-2 pl-1 pr-2">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-60" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
                        </span>
                        <span className="label-eyebrow text-indigo-300/90">
                            AI Manager
                        </span>
                    </div>
                    <Textarea
                        data-testid="ai-command-bar-input"
                        rows={1}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Ask AI anything — 'Why did my rating fall?', 'Reply to today's reviews'…"
                        className="min-h-[36px] max-h-32 resize-none border-none bg-transparent focus-visible:ring-0 text-sm text-slate-100 placeholder:text-slate-500"
                    />
                    <Button
                        data-testid="ai-command-bar-submit-button"
                        onClick={send}
                        disabled={busy || !text.trim() || !currentBusiness}
                        className="rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold gap-1.5 shrink-0"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Sparkles className="h-4 w-4" />
                        )}
                        Ask
                    </Button>
                </div>
                <div className="hidden sm:flex items-center gap-2 px-4 pb-2 flex-wrap">
                    {[
                        "Why did my rating fall?",
                        "Reply to today's reviews",
                        "What are the biggest complaints?",
                        "Generate this week's report",
                    ].map((chip) => (
                        <button
                            key={chip}
                            data-testid={`ai-chip-${chip
                                .toLowerCase()
                                .replace(/[^a-z]+/g, "-")
                                .slice(0, 30)}`}
                            onClick={() => setText(chip)}
                            className="text-[11px] font-medium text-slate-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.08] px-2.5 py-1 rounded-full border border-white/5 transition-colors"
                        >
                            {chip}
                        </button>
                    ))}
                </div>
            </div>
            )}
        </div>
    );
}
